import React, { useState } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Link,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { exportVoicesCampaign } from "../../../Api/voicesCampaigns";
import color from "../../../Constants/colors";
import { useWorkspaceColors } from "../../../Contexts/WorkspaceColorContext";
import { Features } from "../../../Enums/companyStatus";
import { VoicesCampaignStatus, VoicesSearchMode } from "../../../Enums/voices";
import { hasPermission } from "../../../Helpers/authHelpers";
import { VoicesFeed } from "../../../Models/voicesCampaigns";
import {
  useCloseVoicesCampaign,
  useDeleteVoicesCampaign,
  useDeleteVoicesFeed,
  useSaveVoicesFeed,
  useVoicesCampaign,
} from "../../../Query/useVoicesCampaigns";
import Loading from "../../Common/Loading";
import { ComoButton } from "../../Common/ComoButton";
import VoicesCard from "../Common/VoicesCard";
import VoicesConfirmDialog from "../Common/VoicesConfirmDialog";
import VoicesField from "../Common/VoicesField";
import VoicesTermChips from "../Common/VoicesTermChips";
import VoicesPostList from "../Posts/VoicesPostList";
import VoicesTopicList from "../Topics/VoicesTopicList";
import VoicesCampaignDialog from "./VoicesCampaignDialog";
import VoicesCampaignStatusChip from "./VoicesCampaignStatusChip";
import VoicesFeedCheckDialog from "./VoicesFeedCheckDialog";
import VoicesFeedDialog from "./VoicesFeedDialog";
import { VoicesCampaignFormat } from "./voicesCampaignFormat";

type CampaignTab = "topics" | "posts" | "about" | "feeds";

const VoicesCampaignDetail: React.FC = () => {
  const { t } = useTranslation("voicesCampaigns");
  const { colors: workspace } = useWorkspaceColors();
  const navigate = useNavigate();
  const campaignId = Number(useParams<{ campaignId: string }>().campaignId);
  const { data: campaign, isLoading, isError } = useVoicesCampaign(campaignId);
  const canManage = hasPermission(Features.VOICES_MARKETING);
  const canWritePosts = canManage || hasPermission(Features.VOICES_POSTING);

  const closeCampaign = useCloseVoicesCampaign();
  const deleteCampaign = useDeleteVoicesCampaign();
  const saveFeed = useSaveVoicesFeed();
  const deleteFeed = useDeleteVoicesFeed();

  const [tab, setTab] = useState<CampaignTab>("topics");
  const [editOpen, setEditOpen] = useState(false);
  const [confirm, setConfirm] = useState<"close" | "delete" | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [feedDialogOpen, setFeedDialogOpen] = useState(false);
  const [editedFeed, setEditedFeed] = useState<VoicesFeed | null>(null);
  const [feedToDelete, setFeedToDelete] = useState<VoicesFeed | null>(null);
  const [checkedFeed, setCheckedFeed] = useState<VoicesFeed | null>(null);

  if (isLoading) {
    return (
      <Loading
        backgroundColor={color.gray50}
        color1={workspace.accent}
        color2={color.gray900}
      />
    );
  }

  if (isError || !campaign) {
    return (
      <VoicesCard title={t("notFound")}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/voices/campaigns")}
          sx={{ color: workspace.accent, textTransform: "none" }}
        >
          {t("backToCampaigns")}
        </Button>
      </VoicesCard>
    );
  }

  const active = campaign.status === VoicesCampaignStatus.ACTIVE;

  const openFeedDialog = (feed: VoicesFeed | null) => {
    setEditedFeed(feed);
    setFeedDialogOpen(true);
  };

  const runConfirmed = () => {
    if (confirm === "close") {
      closeCampaign.mutate(campaign.id, { onSuccess: () => setConfirm(null) });
    } else if (confirm === "delete") {
      deleteCampaign.mutate(campaign.id, {
        onSuccess: () => navigate("/voices/campaigns"),
      });
    }
  };

  return (
    <>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/voices/campaigns")}
        sx={{ color: color.gray600, textTransform: "none", mb: 1 }}
      >
        {t("backToCampaigns")}
      </Button>

      <VoicesCard
        title={campaign.name}
        subtitle={VoicesCampaignFormat.period(campaign, t("noPeriod"))}
        action={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <VoicesCampaignStatusChip status={campaign.status} />
            <Button
              size="small"
              startIcon={<InsightsOutlinedIcon />}
              onClick={() => navigate(`/voices/results?campaign=${campaign.id}`)}
              sx={{ textTransform: "none", fontWeight: 600, color: color.gray700 }}
            >
              {t("viewResults")}
            </Button>
            {canManage && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EditOutlinedIcon />}
                  onClick={() => setEditOpen(true)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    color: color.gray700,
                    borderColor: color.gray300,
                    "&:hover": { borderColor: color.gray400, backgroundColor: color.gray50 },
                  }}
                >
                  {t("edit")}
                </Button>
                <Tooltip title={t("moreActions")}>
                  <IconButton
                    size="small"
                    aria-label={t("moreActions")}
                    onClick={(event) => setMenuAnchor(event.currentTarget)}
                    sx={{ color: color.gray600 }}
                  >
                    <MoreHorizIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={menuAnchor}
                  open={!!menuAnchor}
                  onClose={() => setMenuAnchor(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  PaperProps={{
                    sx: {
                      mt: 0.5,
                      minWidth: 200,
                      backgroundColor: "white",
                      border: `1px solid ${color.gray200}`,
                      borderRadius: "10px",
                      "& .MuiMenuItem-root": { fontSize: 14, color: color.gray900 },
                      "& .MuiListItemIcon-root": { minWidth: 30, color: color.gray600 },
                    },
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      setMenuAnchor(null);
                      exportVoicesCampaign(campaign.id).catch(() => undefined);
                    }}
                  >
                    <ListItemIcon>
                      <FileDownloadOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    {t("export")}
                  </MenuItem>
                  {active && (
                    <MenuItem
                      onClick={() => {
                        setMenuAnchor(null);
                        setConfirm("close");
                      }}
                    >
                      <ListItemIcon>
                        <LockOutlinedIcon fontSize="small" />
                      </ListItemIcon>
                      {t("closeTitle")}
                    </MenuItem>
                  )}
                  <Divider sx={{ my: 0.5 }} />
                  <MenuItem
                    onClick={() => {
                      setMenuAnchor(null);
                      setConfirm("delete");
                    }}
                    sx={{
                      color: `${color.red700} !important`,
                      "& .MuiListItemIcon-root": { color: color.red700 },
                    }}
                  >
                    <ListItemIcon>
                      <DeleteOutlineIcon fontSize="small" />
                    </ListItemIcon>
                    {t("deleteTitle")}
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        }
      >
        <Tabs
          value={tab}
          onChange={(_e, value) => setTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            mb: 2.5,
            borderBottom: `1px solid ${color.gray200}`,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 700,
              color: color.gray500,
            },
            "& .Mui-selected": { color: `${workspace.accent} !important` },
            "& .MuiTabs-indicator": { backgroundColor: workspace.accent },
          }}
        >
          {campaign.feeds && <Tab value="feeds" label={t("tab_feeds")} />}
          <Tab value="topics" label={t("tab_topics")} />
          <Tab value="posts" label={t("tab_posts")} />
          <Tab value="about" label={t("tab_about")} sx={{ ml: { md: "auto" } }} />
        </Tabs>

        {tab === "topics" && (
          <VoicesTopicList
            campaignId={campaign.id}
            canManage={canManage}
            canWritePosts={canWritePosts && active}
          />
        )}

        {tab === "posts" && (
          <VoicesPostList campaignId={campaign.id} canWrite={canWritePosts && active} />
        )}

        {tab === "about" && (
          <Stack spacing={2.5}>
            <VoicesField label={t("description")}>
              <Typography variant="body2" sx={{ color: color.gray700, whiteSpace: "pre-wrap" }}>
                {campaign.description || t("noDescription")}
              </Typography>
            </VoicesField>
            <Box sx={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <VoicesField label={t("period")}>
                <Typography variant="body2" sx={{ color: color.gray700 }}>
                  {VoicesCampaignFormat.period(campaign, t("noPeriod"))}
                </Typography>
              </VoicesField>
              <VoicesField label={t("searchMode")}>
                <Typography variant="body2" sx={{ color: color.gray700 }}>
                  {campaign.search_mode === VoicesSearchMode.QMS_AGENT
                    ? t("searchModeAgent")
                    : t("searchModeClaude")}
                </Typography>
              </VoicesField>
            </Box>
            <VoicesField label={t("searchTerms")}>
              <VoicesTermChips terms={campaign.search_terms} emptyText={t("noSearchTerms")} />
            </VoicesField>
            <VoicesField label={t("topicFocus")}>
              <Typography variant="body2" sx={{ color: color.gray700, whiteSpace: "pre-wrap" }}>
                {campaign.topic_focus || t("noTopicFocus")}
              </Typography>
            </VoicesField>
            <VoicesField label={t("instructions")}>
              <Typography variant="body2" sx={{ color: color.gray700, whiteSpace: "pre-wrap" }}>
                {campaign.instructions || t("noInstructions")}
              </Typography>
            </VoicesField>
          </Stack>
        )}

        {tab === "feeds" && campaign.feeds && (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
                mb: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: color.gray600 }}>
                {t("feedsSubtitle")}
              </Typography>
              {canManage && (
                <ComoButton startIcon={<AddIcon />} onClick={() => openFeedDialog(null)}>
                  {t("newFeed")}
                </ComoButton>
              )}
            </Box>
            {!campaign.feeds.length ? (
              <Typography variant="body2" sx={{ color: color.gray500 }}>
                {t("noFeeds")}
              </Typography>
            ) : (
              <Table size="small">
                <TableBody>
                  {campaign.feeds.map((feed) => (
                    <TableRow key={feed.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {feed.name}
                        </Typography>
                        <Link
                          href={feed.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="caption"
                          sx={{ color: color.gray500 }}
                        >
                          {feed.url}
                        </Link>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        <Switch
                          size="small"
                          checked={feed.active}
                          disabled={!canManage || saveFeed.isPending}
                          onChange={(e) =>
                            saveFeed.mutate({
                              campaignId: campaign.id,
                              feedId: feed.id,
                              data: { active: e.target.checked },
                            })
                          }
                          sx={{
                            "& .Mui-checked": { color: `${workspace.accent} !important` },
                            "& .Mui-checked + .MuiSwitch-track": {
                              backgroundColor: `${workspace.accent} !important`,
                            },
                          }}
                        />
                        {canManage && (
                          <>
                            <Tooltip title={t("checkFeed")}>
                              <IconButton
                                size="small"
                                onClick={() => setCheckedFeed(feed)}
                                sx={{ color: workspace.accent }}
                              >
                                <ManageSearchIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <IconButton
                              size="small"
                              onClick={() => openFeedDialog(feed)}
                              sx={{ color: workspace.accent }}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => setFeedToDelete(feed)}
                              sx={{ color: color.gray500 }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </VoicesCard>

      <VoicesCampaignDialog
        open={editOpen}
        campaign={campaign}
        onClose={() => setEditOpen(false)}
      />
      <VoicesFeedDialog
        open={feedDialogOpen}
        campaignId={campaign.id}
        feed={editedFeed}
        onClose={() => setFeedDialogOpen(false)}
      />
      <VoicesFeedCheckDialog
        campaignId={campaign.id}
        feed={checkedFeed}
        hasSearchTerms={campaign.search_terms.length > 0}
        onClose={() => setCheckedFeed(null)}
      />
      <VoicesConfirmDialog
        open={confirm !== null}
        title={confirm === "close" ? t("closeTitle") : t("deleteTitle")}
        text={
          confirm === "close"
            ? t("closeConfirm", { name: campaign.name })
            : t("deleteConfirm", { name: campaign.name })
        }
        confirmLabel={confirm === "close" ? t("close") : t("delete")}
        pending={closeCampaign.isPending || deleteCampaign.isPending}
        onConfirm={runConfirmed}
        onClose={() => setConfirm(null)}
      />
      <VoicesConfirmDialog
        open={!!feedToDelete}
        title={t("deleteFeedTitle")}
        text={t("deleteFeedConfirm", { name: feedToDelete?.name })}
        confirmLabel={t("delete")}
        pending={deleteFeed.isPending}
        onConfirm={() =>
          feedToDelete &&
          deleteFeed.mutate(
            { campaignId: campaign.id, feedId: feedToDelete.id },
            { onSuccess: () => setFeedToDelete(null) },
          )
        }
        onClose={() => setFeedToDelete(null)}
      />
    </>
  );
};

export default VoicesCampaignDetail;
